/* -------------------------------------------------------------------------- */
/*                                Button styles                               */
/* -------------------------------------------------------------------------- */

@import './bq-button.variables';

@layer components {
  // Common button base style
  .bq-button {
    @apply box-border flex cursor-[inherit] items-center justify-center font-medium leading-regular;
    @apply rounded-[--bq-button--border-radius] border-[length:--bq-button--border-width] border-[color:--bq-button--border-color];
    @apply transition-[background-color,border-color,color] duration-300 ease-in-out;
    // `DISABLED` state
    @apply disabled:cursor-not-allowed disabled:opacity-60;
    // `FOCUS` state
    @apply focus-visible:focus;
    // Internal workaround to allow overriding icon color via CSS variables from slotted bq-buttons
    // @see: packages/beeq/src/components/side-menu/scss/bq-side-menu.scss
    @apply [&_::slotted(bq-icon)]:[--bq-icon--color:_var(--bq-button--icon-color,_currentColor)];
    // On hover, update the icon color if custom property is defined
    &:hover:not(:disabled) ::slotted(bq-icon) {
      --bq-icon--color: var(--bq-button-hover--icon-color, var(--bq-button--icon-color, currentColor));
    }
  }
}

:host {
  @apply is-auto relative inline-block cursor-pointer rounded-[--bq-button--border-radius];
}

:host([block]),
.block {
  @apply is-full;
}

/* -------------------------------------------------------------------------- */
/*                                 Appearance                                 */
/* -------------------------------------------------------------------------- */

.bq-button--primary {
  @apply bg-ui-brand text-alt;
  // Primary `HOVER` state
  @apply hover:enabled:bg-hover-ui-brand;
  // Primary `ACTIVE` state
  @apply active:enabled:bg-active-ui-brand active:enabled:hover:bg-active-ui-brand;
}

.bq-button--primary.ghost {
  @apply border-m border-solid border-brand bg-transparent text-brand;
  // Primary `HOVER` state
  @apply hover:enabled:bg-hover-ui-primary;
  // Primary `ACTIVE` state
  @apply active:enabled:bg-active-ui-primary active:enabled:hover:bg-active-ui-primary;
}

.bq-button--primary.danger {
  @apply bg-ui-danger text-alt;
  // Primary `HOVER` state
  @apply hover:enabled:bg-hover-ui-danger;
  // Primary `ACTIVE` state
  @apply active:enabled:bg-active-ui-danger active:enabled:hover:bg-active-ui-danger;
}

.bq-button--secondary {
  @apply bg-ui-secondary text-primary;
  // Secondary `HOVER` state
  @apply hover:enabled:bg-hover-ui-secondary;
  // Secondary `ACTIVE` state
  @apply active:enabled:bg-active-ui-secondary active:enabled:hover:bg-active-ui-secondary;
}

.bq-button--secondary.ghost {
  @apply border-m border-solid border-tertiary bg-transparent text-primary;
  // Secondary `HOVER` state
  @apply hover:enabled:bg-hover-ui-secondary;
  // Secondary `ACTIVE` state
  @apply active:enabled:bg-active-ui-secondary active:enabled:hover:bg-active-ui-secondary;
}

.bq-button--link {
  @apply bg-transparent text-brand no-underline;
  // Primary `HOVER` state
  @apply [&:not(.disabled)]:hover:bg-hover-ui-primary;
  // Primary `ACTIVE` state
  @apply [&:not(.disabled)]:active:bg-active-ui-secondary [&:not(.disabled)]:active:hover:bg-active-ui-secondary;
  // HTML `<a>` elements does not have a `disabled` state so we need to handle it manually
  &.disabled {
    @apply cursor-not-allowed opacity-60;
  }
}

.bq-button--text {
  @apply bg-transparent text-primary;
  // Primary `HOVER` state
  @apply hover:enabled:bg-hover-ui-primary;
  // Primary `ACTIVE` state
  @apply active:enabled:bg-active-ui-secondary active:enabled:hover:bg-active-ui-secondary;
}

/* -------------------------------------------------------------------------- */
/*                                    Size                                    */
/* -------------------------------------------------------------------------- */

.only-icon {
  @apply aspect-square;
}

.small {
  @apply text-[length:--bq-button--small-font-size] p-b-[--bq-button--small-paddingY] p-i-[--bq-button--small-paddingX];

  &.only-icon {
    @apply p-[--bq-button--small-paddingY];
  }
}

.medium {
  @apply text-[length:--bq-button--medium-font-size] p-b-[--bq-button--medium-paddingY] p-i-[--bq-button--medium-paddingX];

  &.only-icon {
    @apply p-[--bq-button--medium-paddingY];
  }
}

.large {
  @apply text-[length:--bq-button--large-font-size] p-b-[--bq-button--large-paddingY] p-i-[--bq-button--large-paddingX];

  &.only-icon {
    @apply p-[--bq-button--large-paddingY];
  }
}

/* -------------------------------------------------------------------------- */
/*                                    Label                                   */
/* -------------------------------------------------------------------------- */

.bq-button__label ::slotted(bq-icon) {
  @apply flex;
}

.content-left {
  @apply justify-start;
}

.content-right {
  @apply justify-end;
}

/* -------------------------------------------------------------------------- */
/*                           Prefix and Suffix slots                          */
/* -------------------------------------------------------------------------- */

.bq-button__prefix,
.bq-button__suffix {
  @apply pointer-events-none flex shrink-0 grow-0 basis-auto items-center;
}

.bq-button.has-prefix .bq-button__label {
  @apply ps-xs2;
}

.bq-button.has-suffix .bq-button__label {
  @apply pe-xs2;
}

/* -------------------------------------------------------------------------- */
/*                                   Loading                                  */
/* -------------------------------------------------------------------------- */

.bq-button.loading {
  @apply relative cursor-wait;
}

.bq-button.loading .bq-button__prefix,
.bq-button.loading .bq-button__label,
.bq-button.loading .bq-button__suffix {
  @apply invisible;
}

/* -------------------------------------------------------------------------- */
/*                                Spinner icon                                */
/* -------------------------------------------------------------------------- */

.bq-button__loader {
  @include animation-spin;
  @apply absolute;
}
